import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Unplug, PlugZap, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PROVIDER_LABELS = {
  wix: "Wix",
  github: "GitHub",
  ftp: "FTP",
  vps: "VPS API",
  gsc: "Search Console",
  ga4: "GA4",
};

export const SiteConnectionsPanel = ({ siteId, onChanged }) => {
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}/connections`);
      setConns(data.connections || []);
    } catch {
      setConns([]);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const disconnect = async (provider) => {
    setBusy(provider);
    try {
      await api.delete(`/sites/${siteId}/connections/${provider}`);
      toast.success(`${PROVIDER_LABELS[provider] || provider} déconnecté. Les audits, historiques et brouillons du site sont conservés.`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la déconnexion");
    } finally {
      setBusy("");
    }
  };

  if (!conns || conns.length === 0) return null;

  return (
    <div className="pt-2 mt-2 border-t border-slate-100" data-testid={`site-connections-${siteId}`}>
      <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <PlugZap className="w-3 h-3" /> Connexions
      </div>
      <div className="flex flex-wrap gap-1.5">
        {conns.map((c) => (
          <div
            key={c.provider}
            data-testid={`connection-${c.provider}-${siteId}`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${
              c.has_credentials
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            <span className="font-medium">{PROVIDER_LABELS[c.provider] || c.provider}</span>
            <span className="text-[10px] opacity-70">
              {c.has_credentials ? "connectée" : "config partielle"}
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid={`disconnect-${c.provider}-${siteId}`}
                  title={`Déconnecter ${PROVIDER_LABELS[c.provider] || c.provider}`}
                  disabled={busy === c.provider}
                  className="ml-0.5 p-0.5 rounded hover:bg-red-100 hover:text-red-600 transition-colors disabled:opacity-50"
                >
                  {busy === c.provider ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unplug className="w-3 h-3" />}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Déconnecter {PROVIDER_LABELS[c.provider] || c.provider} de ce site ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Les identifiants de cette intégration seront supprimés pour ce site uniquement.
                    Les audits, l'historique SEO, les corrections et les brouillons du site sont conservés.
                    Vous pourrez reconnecter l'intégration à tout moment.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid={`confirm-disconnect-${c.provider}-${siteId}`}
                    onClick={() => disconnect(c.provider)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Déconnecter
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SiteConnectionsPanel;
